import React from 'react';
import Slider from 'react-slick';
import { CountryCode } from '@dayetopia/library/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { EXTERNAL_LINK_SVG, PPC_DIAGNOSIS, PPC_PAIN, PPC_PAINKILLERS } from '@constants';
import { PageSlice } from '@contracts';
import Lightbulb from '@images/vms-account/vms-icons/Lightbulb.svg';
import useNavigate from '@utils/useNavigate';
import Button from './Button';
import Colors from './Colors';
import LinkTranslated from './LinkTranslated';
import { BodyLG } from './TextTypes';
import useDesktop from './useDesktop';

const settings = {
  arrows: false,
  dots: true,
  infinite: false,
  arrow: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1
};

interface USP {
  title: string;
  descriptionKey: string;
  icon: string;
  link: string;
}

const USPsInfo: USP[] = [
  {
    title: '9 in 10',
    descriptionKey: 'ppc-did-you-know.period-pain',
    icon: PPC_PAIN,
    link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC8525260/'
  },
  {
    title: '55%',
    descriptionKey: 'ppc-did-you-know.period-painkillers',
    icon: PPC_PAINKILLERS,
    link: 'https://www.cochrane.org/CD001751/MENSTR_nonsteroidal-anti-inflammatory-drugs-dysmenorrhoea/'
  },
  {
    title: '7–10 years',
    descriptionKey: 'ppc-did-you-know.period-diagnosis',
    icon: PPC_DIAGNOSIS,
    link: 'https://pubmed.ncbi.nlm.nih.gov/8671344/'
  }
];

interface Props {
  slice: PageSlice;
  country?: CountryCode;
}

const PPCDidYouKnow: React.FC<Props> = ({ slice, country }) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const isDesktop = useDesktop(768);

  const handleButtonClick = () => {
    navigateWrapper('/ppc-questionnaire/');
  };

  const renderPackageBox = (usp: USP, index: number) => (
    <BoxContainer key={index}>
      <img src={usp.icon} height="40px" width="40px" />
      <BigText>{usp.title}</BigText>
      <DescriptionText>{t(usp.descriptionKey)}</DescriptionText>
      <Link>
        <LinkTranslated to={usp.link} targetBlank>
          {t('ppc-did-you-know.source')}
        </LinkTranslated>
      </Link>
    </BoxContainer>
  );

  return (
    <Container>
      <Inner>
        <RowWrap>
          <Lightbulb />
          <Title>{t('ppc-did-you-know.did-you-know')}</Title>
        </RowWrap>
        {isDesktop ? (
          <PackagesWrap>{USPsInfo.map((usp: any, index: number) => renderPackageBox(usp, index))}</PackagesWrap>
        ) : (
          <SliderWrap>
            <Slider {...settings}>{USPsInfo.map((usp: any, index: number) => renderPackageBox(usp, index))}</Slider>
          </SliderWrap>
        )}
        <BodyLG>{t('ppc-did-you-know.quality-of-life')}</BodyLG>
        {country === CountryCode.GB && (
          <Button style={{ marginTop: '40px' }} onClick={() => handleButtonClick()}>
            {t('ppc-did-you-know.start-here')}
          </Button>
        )}
      </Inner>
    </Container>
  );
};

export default PPCDidYouKnow;

const Container = styled.div`
  width: 100%;
  display: flex;
  padding: 30px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    padding: 0;

    &.align-carousel {
      padding: 0 0 60px 0;
    }
  }
`;

const Inner = styled.div`
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 0 20px;
  }
`;

const SliderWrap = styled.div`
  max-width: 1200px;
  display: flex;
  justify-content: space-between;
  margin: auto;
  gap: 40px;
  justify-content: center;

  @media only screen and (max-width: 1220px) {
    width: 90vw;
    justify-content: center;

    & .slick-slider {
      display: flex;
      justify-content: center;
      width: 100vw;
      flex-wrap: wrap;
    }
    .slick-dots {
      margin-top: 40px;
      position: relative;
      width: 100vw;
    }
    & .slick-slide.slick-active {
      opacity: 1;
    }

    .slick-slide {
      height: auto;

      & > div {
        height: 100%;
        display: flex;
        justify-content: center;
      }
    }
  }
`;

const Title = styled.div`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

const BoxContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  gap: 20px;
  width: 346.67px;
  border: 1px solid ${Colors.ecru};
  border-radius: 20px;

  @media (max-width: 1024px) {
    width: 90% !important;
    display: flex !important;
  }
`;

const DescriptionText = styled(BodyLG)`
  max-width: 250px;

  @media (max-width: 1024px) {
    font-size: 13px;
  }
`;

const PackagesWrap = styled.div`
  display: flex;
  flex-direction: row;
  padding: 40px;
  gap: 40px;
`;

const RowWrap = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  span {
    font-family: BrownStd;
    font-weight: 700;
    font-size: 14px;
    line-height: 150%;
  }

  @media (max-width: 1024px) {
    padding-bottom: 20px;
  }
`;

const BigText = styled.div`
  font-family: Domaine;
  font-style: normal;
  font-weight: 600;
  font-size: 50px;
  line-height: 110%;
  display: flex;
  align-items: flex-end;
  text-align: center;
  letter-spacing: -0.01em;

  @media (max-width: 1024px) {
    font-size: 34px;
  }
`;

const Link = styled.div`
  a {
    font-family: BrownStd;
    font-weight: 700;
    font-size: 14px;
    line-height: 140%;
    color: ${Colors.lilac};

    :hover {
      text-decoration: underline;
    }

    &:after {
      content: '';
      background-image: url(${EXTERNAL_LINK_SVG});
      background-repeat: no-repeat;
      background-position: center;
      width: 15px;
      height: 15px;
      display: inline-block;
      margin-left: 5px;
      vertical-align: -2px;
    }
  }
}`;
